/**
 * Dialog "EDIT PURCHASE PLAN" — mirror Access form
 * `frmMatchingPSMainSubFormEditPlan_p` (file
 * `frontend/plan/access_all/form_frmMatchingPSMainSubFormEditPlan_p.txt`).
 *
 * Đối xứng với `EditSalePlanDialog`: thay vì chỉnh plan của contract Sale, dialog
 * này chỉnh plan của contract Purchase. Combo "Choose Sale" thay cho "Choose
 * Purchase", insert vào `match_p_stock` + `match_direct_ps` (p_side=contract_no,
 * s_side=ins_side) thay vì `match_s_stock`.
 *
 * VBA logic (cuối form spec):
 * - `divide_number()`     → chia đều quantity_kg cho số checkbox được tick,
 *                            dư mod gắn vào checkbox đầu tiên (Direct > NT > BL).
 * - `btnSavePlan_Click()` → 6 validation tuần tự + soft-delete + insert nhiều rows.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '../../../hooks/useSnackbar'
import { titleCaseName } from '../../../utils/text'
import {
  useDeleteDoFromPlanMutation,
  useLazyGetDosByMatchQuery,
  useGetActiveDirectSaleQuery,
  useGetDosByContractQuery,
  useGetEditPlanPSaleCandidatesQuery,
  useSaveEditPlanPMutation,
  type EditPlanSaleCandidate,
} from '../../../store/api/matchingPsApi'
import ConfirmDialog from '../../../components/ConfirmDialog'
import CreateDoFromPlanDialog, {
  type DoDraftSeed,
  type EditDoRow,
} from './CreateDoFromPlanDialog'

const ACCENT = '#1e88e5' // P column accent (blue)

export interface EditPurchasePlanDialogProps {
  open: boolean
  onClose: () => void
  /** Row đang được chọn ở table Purchase (đã camelCase từ `_row_to_item`). */
  row: Record<string, unknown> | null
  /** Permission gating (Layer 4) — false → ẩn Save + show read-only banner. */
  canEdit?: boolean
}

function toNum(v: unknown): number {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function formatThousand(n: number): string {
  if (!Number.isFinite(n)) return ''
  return Math.round(n).toLocaleString('en-US')
}

function formatAccessDate(v: unknown): string {
  if (!v) return ''
  const d = dayjs(String(v))
  return d.isValid() ? d.format('DD-MMM-YYYY') : String(v)
}

/** ins_side_cb display: `contract_no - <pending #,###> - quality_code - category`. */
function formatSaleOption(c: EditPlanSaleCandidate): string {
  const parts = [
    c.contract_no,
    formatThousand(toNum(c.s_plan_pending)),
    c.quality_code ?? '',
    c.category ?? '',
  ]
  return parts.filter((p) => p !== '').join(' - ')
}

export default function EditPurchasePlanDialog({
  open,
  onClose,
  row,
  canEdit = true,
}: EditPurchasePlanDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {open && row ? (
        <EditPurchasePlanDialogContent
          key={String(row.contractNo ?? '')}
          row={row}
          onClose={onClose}
          canEdit={canEdit}
        />
      ) : null}
    </Dialog>
  )
}

interface EditPurchasePlanDialogContentProps {
  row: Record<string, unknown>
  onClose: () => void
  canEdit: boolean
}

function EditPurchasePlanDialogContent({ row, onClose, canEdit }: EditPurchasePlanDialogContentProps) {
  const snack = useSnackbar()

  // ── Read-only fields lấy thẳng từ selected row ─────────────────────────
  const contractNo = String(row.contractNo ?? '')
  const cpName = String(row.cpName ?? '')
  const contractDate = formatAccessDate(row.contractDate)
  const latestDelivery = formatAccessDate(row.latestDelivery)
  const quantityKg = toNum(row.quantityKg)
  const qualityCode = String(row.qualityCode ?? '')
  const grade = String(row.category ?? '')
  const instructedBL = toNum(row.pInstructBaoloc)
  const instructedNT = toNum(row.pInstructNhontrach)
  const instructedDirect = toNum(row.pDirectS)

  // ── Edit state ─ load sẵn Tick Plan + Số từ plan đã có (instructed values). ──
  const [chbBL, setChbBL] = useState(() => instructedBL > 0)
  const [chbNT, setChbNT] = useState(() => instructedNT > 0)
  const [chbDirect, setChbDirect] = useState(() => instructedDirect > 0)
  const [insBL, setInsBL] = useState<string>(() => (instructedBL > 0 ? String(instructedBL) : '0'))
  const [insNT, setInsNT] = useState<string>(() => (instructedNT > 0 ? String(instructedNT) : '0'))
  const [insDirect, setInsDirect] = useState<string>(
    () => (instructedDirect > 0 ? String(instructedDirect) : '0'),
  )
  const [insSide, setInsSide] = useState<string>('')
  const [matchDate, setMatchDate] = useState<Dayjs | null>(() => dayjs())
  const [note, setNote] = useState<string>('')

  // Sales — fetch khi dialog open (inner mount = open).
  const candidatesQuery = useGetEditPlanPSaleCandidatesQuery()
  const candidates = useMemo(() => candidatesQuery.data ?? [], [candidatesQuery.data])

  const selectedCandidate = useMemo(
    () => candidates.find((c) => c.contract_no === insSide) ?? null,
    [candidates, insSide],
  )
  const insSidePending = toNum(selectedCandidate?.s_plan_pending)

  // ── divide_number — VBA: chia đều quantity_kg / số tick, mod (kg dư) dồn ──
  // vào checkbox đầu tiên theo thứ tự Direct > NT > BL.
  const runDivide = (nextBL: boolean, nextNT: boolean, nextDirect: boolean) => {
    const count = (nextBL ? 1 : 0) + (nextNT ? 1 : 0) + (nextDirect ? 1 : 0)
    if (count === 0) {
      setInsBL('0')
      setInsNT('0')
      setInsDirect('0')
      if (!nextDirect) setInsSide('')
      return
    }
    const divide = Math.floor(quantityKg / count)
    const mod = quantityKg - divide * count
    let modCheck = true
    let nextDirectKg = 0
    let nextNTKg = 0
    let nextBLKg = 0
    if (nextDirect && divide > 0) {
      nextDirectKg = divide
      if (mod !== 0 && modCheck) {
        nextDirectKg += mod
        modCheck = false
      }
    } else if (!nextDirect) {
      setInsSide('')
    }
    if (nextNT && divide > 0) {
      nextNTKg = divide
      if (mod !== 0 && modCheck) {
        nextNTKg += mod
        modCheck = false
      }
    }
    if (nextBL && divide > 0) {
      nextBLKg = divide
      if (mod !== 0 && modCheck) {
        nextBLKg += mod
        modCheck = false
      }
    }
    setInsDirect(String(nextDirectKg))
    setInsNT(String(nextNTKg))
    setInsBL(String(nextBLKg))
  }

  const toggleChbBL = (next: boolean) => {
    setChbBL(next)
    runDivide(next, chbNT, chbDirect)
  }
  const toggleChbNT = (next: boolean) => {
    setChbNT(next)
    runDivide(chbBL, next, chbDirect)
  }
  const toggleChbDirect = (next: boolean) => {
    setChbDirect(next)
    runDivide(chbBL, chbNT, next)
    // Tick Direct mà contract đã có Direct với 1 Sale trước đó → autoload Select Sale.
    if (next && !insSide.trim() && activeDirect?.sSide) {
      setInsSide(activeDirect.sSide)
    }
  }

  // ── Save mutation ──────────────────────────────────────────────────────
  const [save, { isLoading: saving }] = useSaveEditPlanPMutation()

  // Create DO sau khi Save có phần Direct P-S (ins_direct>0).
  const [doOpen, setDoOpen] = useState(false)
  const [doSeeds, setDoSeeds] = useState<DoDraftSeed[]>([])
  const [doMode, setDoMode] = useState<'create' | 'edit'>('create')
  const [doEditRows, setDoEditRows] = useState<EditDoRow[]>([])
  // Confirm "xoá DO + update plan" khi Direct=0 mà contract còn DO đã book.
  const [confirmDelSaveOpen, setConfirmDelSaveOpen] = useState(false)
  const [deleteDoRow, { isLoading: deletingDos }] = useDeleteDoFromPlanMutation()
  const [fetchDosByMatch] = useLazyGetDosByMatchQuery()

  // DO đã book cho Direct P-S của contract này → text alert cho user biết.
  const { data: existingDos } = useGetDosByContractQuery(
    { contractNo, side: 'P' },
    { skip: !contractNo },
  )

  // Direct match active sẵn có (nếu contract đã match Direct với 1 Sale trước đó)
  // → autoload combo Select Sale khi tick Direct P-S.
  const { data: activeDirect } = useGetActiveDirectSaleQuery(
    { contractNo },
    { skip: !contractNo },
  )
  // Direct đã tick sẵn (plan cũ) → load luôn Select Sale từ direct match active.
  useEffect(() => {
    if (chbDirect && !insSide.trim() && activeDirect?.sSide) {
      setInsSide(activeDirect.sSide)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDirect])

  // Thực thi save plan. `deleteExistingDos` = true: xoá hết DO cũ trước (TH Direct=0).
  const performSaveAndDo = async (deleteExistingDos: boolean) => {
    const baolocKg = toNum(insBL)
    const nhontrachKg = toNum(insNT)
    const directKg = toNum(insDirect)
    const hasDos = (existingDos?.length ?? 0) > 0
    try {
      if (deleteExistingDos && hasDos) {
        for (const d of existingDos ?? []) {
          await deleteDoRow({ doId: d.deliveryOrderScsId }).unwrap()
        }
      }
      const res = await save({
        contract_no: contractNo,
        match_date: matchDate ? matchDate.format('YYYY-MM-DD') : '',
        note: note.trim() || undefined,
        ins_instruct_baoloc: chbBL ? baolocKg : 0,
        ins_instruct_nhontrach: chbNT ? nhontrachKg : 0,
        ins_direct: chbDirect ? directKg : 0,
        ins_side: chbDirect ? insSide.trim() : undefined,
        quantity_kg: quantityKg,
      }).unwrap()
      snack.success('Successfully Updated')
      const dm = res.direct_match
      if (dm && dm.match_id != null && dm.match_kg > 0) {
        setDoSeeds([
          {
            saleNo: dm.s_side,
            buyer: '',
            toWh: cpName,
            doQty: dm.match_kg,
            planRef: dm.p_side,
            planRefSub: cpName ? `Shipper: ${cpName}` : '',
            planKg: dm.match_kg,
            matchType: dm.match_type,
            matchId: dm.match_id,
          },
        ])
        // Direct>0 + không xoá → load DO theo MATCH_ID (BE re-point DO sang match
        // mới) → edit mode dùng lại đúng DO của match đó.
        if (!deleteExistingDos) {
          const dos = await fetchDosByMatch({ matchId: dm.match_id, matchType: 'direct' })
            .unwrap()
            .catch(() => [])
          if (dos.length > 0) {
            setDoMode('edit')
            setDoEditRows(
              dos.map((d) => ({
                doId: d.deliveryOrderScsId,
                doNo: d.doNo ?? '',
                toWh: d.fromWarehouse ?? '',
                doQty: Number(d.doQty) || 0,
                note: d.note ?? '',
                planId: 0,
              })),
            )
          } else {
            setDoMode('create')
            setDoEditRows([])
          }
        } else {
          setDoMode('create')
          setDoEditRows([])
        }
        setDoOpen(true)
      } else {
        onClose()
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'data' in e
          ? String((e as { data?: { detail?: string } }).data?.detail ?? 'Save failed.')
          : e instanceof Error
            ? e.message
            : 'Save failed.'
      snack.error(msg)
    }
  }

  const handleSave = () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    const directKg = toNum(insDirect)
    const baolocKg = toNum(insBL)
    const nhontrachKg = toNum(insNT)

    if (!chbBL && !chbNT && !chbDirect) {
      snack.warning('Please select at least one type to plan!')
      return
    }
    if (directKg + baolocKg + nhontrachKg > quantityKg) {
      snack.warning('Recheck: Total Input Quantity <= Quantity Kg!')
      return
    }
    if (chbDirect && !insSide.trim()) {
      snack.warning('Please choose Sale (for Direct P-S)!')
      return
    }
    if (chbDirect && directKg > insSidePending && insSidePending > 0) {
      snack.warning('Recheck: Direct P-S <= Sale Pending Quantity!')
      return
    }
    if (directKg < 0 || baolocKg < 0 || nhontrachKg < 0) {
      snack.warning('Recheck: quantities must be >= 0!')
      return
    }
    if (!matchDate || !matchDate.isValid()) {
      snack.warning('Please choose Match Date!')
      return
    }

    const effectiveDirect = chbDirect ? directKg : 0
    const hasDos = (existingDos?.length ?? 0) > 0
    // TH1: Direct P-S = 0 mà contract còn DO đã book → confirm xoá DO + update plan.
    if (effectiveDirect === 0 && hasDos) {
      setConfirmDelSaveOpen(true)
      return
    }
    // TH2 (Direct>0 + có DO) hoặc bình thường → save; performSaveAndDo lo phần DO.
    void performSaveAndDo(false)
  }

  const readOnlyField = {
    size: 'small' as const,
    InputProps: { readOnly: true },
    InputLabelProps: { shrink: true },
    fullWidth: true,
  }

  return (
    <>
      <DialogTitle
        title="EDIT PURCHASE PLAN"
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 0.75, sm: 1.25 },
          bgcolor: alpha(ACCENT, 0.1),
          borderBottom: `2px solid ${alpha(ACCENT, 0.55)}`,
          fontSize: { xs: '0.85rem', sm: '1rem' },
          fontWeight: 700,
          letterSpacing: 0.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        EDIT PURCHASE PLAN
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.25, sm: 2 } }}>
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Read-only — you don't have edit permission for this module.
          </Alert>
        ) : null}
        {/* ── Read-only context block ─────────────────────────────────── */}
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Purchase Contract" value={contractNo} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Contract Date" value={contractDate} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Supplier" value={titleCaseName(cpName)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              {...readOnlyField}
              label="ContractQuant"
              value={formatThousand(quantityKg)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Quality" value={qualityCode} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Grade" value={grade} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Latest Delivery" value={latestDelivery} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ pt: 0.5, alignItems: 'center' }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: { xs: 'auto', sm: 110 } }}>
                Already instructed:
              </Typography>
              <Chip
                size="small"
                label={`Bảo Lộc · ${formatThousand(instructedBL)}`}
                sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`Nhơn Trạch · ${formatThousand(instructedNT)}`}
                sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`Direct (to S) · ${formatThousand(instructedDirect)}`}
                sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, fontWeight: 600 }}
              />
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}
          >
            Delete Old plan &amp; Insert new plan
          </Typography>
        </Divider>

        {/* ── Editable 3-row plan ──────────────────────────────────────
            Layout: 3 cột đều nhau 4/4/4 trong một Paper "table" có border
            + divider giữa header và từng row → giúp 3 cột nối với nhau
            visually (header label thẳng cột với input bên dưới). */}
        <Box
          sx={{
            border: (t) => `1px solid ${alpha(t.palette.text.primary, 0.12)}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* Header row */}
          <Grid
            container
            sx={{
              display: { xs: 'none', md: 'flex' },
              bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
              borderBottom: (t) => `1px solid ${alpha(t.palette.text.primary, 0.1)}`,
            }}
          >
            <Grid item md={4} sx={{ px: 1.25, py: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Tick Plan
              </Typography>
            </Grid>
            <Grid
              item
              md={3}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Input Quantity (kg)
              </Typography>
            </Grid>
            <Grid
              item
              md={5}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Choose Sale (for Direct P-S)
              </Typography>
            </Grid>
          </Grid>

          {/* Row: Bảo Lộc */}
          <Grid
            container
            alignItems="center"
            sx={{
              borderBottom: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
            }}
          >
            <Grid item xs={12} md={4} sx={{ px: 1.25, py: 0.75 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={chbBL}
                    onChange={(e) => toggleChbBL(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Instruction - Bảo Lộc</Typography>}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                label="Input Quantity (kg)"
                value={formatThousand(toNum(insBL))}
                onChange={(e) => setInsBL(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!chbBL}
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
                color: 'text.disabled',
              }}
            >
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                — not applicable
              </Typography>
            </Grid>
          </Grid>

          {/* Row: Nhơn Trạch */}
          <Grid
            container
            alignItems="center"
            sx={{
              borderBottom: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
            }}
          >
            <Grid item xs={12} md={4} sx={{ px: 1.25, py: 0.75 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={chbNT}
                    onChange={(e) => toggleChbNT(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Instruction - Nhơn Trạch</Typography>}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                label="Input Quantity (kg)"
                value={formatThousand(toNum(insNT))}
                onChange={(e) => setInsNT(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!chbNT}
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
                color: 'text.disabled',
              }}
            >
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                — not applicable
              </Typography>
            </Grid>
          </Grid>

          {/* Row: Direct P-S */}
          <Grid container alignItems="center">
            <Grid item xs={12} md={4} sx={{ px: 1.25, py: 0.75 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={chbDirect}
                    onChange={(e) => toggleChbDirect(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Direct P-S</Typography>}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                label="Input Quantity (kg)"
                value={formatThousand(toNum(insDirect))}
                onChange={(e) => setInsDirect(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!chbDirect}
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <Autocomplete
                size="small"
                fullWidth
                options={
                  insSide && !candidates.some((c) => c.contract_no === insSide)
                    ? [{ contract_no: insSide } as typeof candidates[number], ...candidates]
                    : candidates
                }
                value={candidates.find((c) => c.contract_no === insSide)
                  ?? (insSide ? ({ contract_no: insSide } as typeof candidates[number]) : null)}
                onChange={(_e, v) => setInsSide(v?.contract_no ?? '')}
                getOptionLabel={(o) => o ? formatSaleOption(o) : ''}
                isOptionEqualToValue={(a, b) => a.contract_no === b.contract_no}
                disabled={!chbDirect}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Sale"
                    InputLabelProps={{ shrink: true }} />
                )}
              />
            </Grid>
          </Grid>
          {/* Direct ticked: note danh sách DO đã book (p_direct_s_lst) + cảnh báo thiếu. */}
          {chbDirect && (String(row.pDirectSLst ?? '') || instructedDirect > toNum(row.pDirectSDo)) ? (
            <Box sx={{ px: 1.25, pb: 0.75, bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
              {String(row.pDirectSLst ?? '') ? (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Booked DO: <b>{String(row.pDirectSLst)}</b>
                </Typography>
              ) : null}
              {instructedDirect > toNum(row.pDirectSDo) ? (
                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                  ⚠ Planned but DO not fully booked — add DO after saving.
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── Match Date + Note ───────────────────────────────────────── */}
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={4}>
            <DatePicker
              label="Match Date"
              format="DD-MMM-YYYY"
              value={matchDate}
              onChange={(d) => setMatchDate(d)}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              size="small"
              fullWidth
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 1,
            bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
            border: (t) => `1px dashed ${alpha(t.palette.text.primary, 0.15)}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Sum input:{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {formatThousand(
                (chbBL ? toNum(insBL) : 0) +
                  (chbNT ? toNum(insNT) : 0) +
                  (chbDirect ? toNum(insDirect) : 0),
              )}
            </Box>{' '}
            / ContractQuant: <strong>{formatThousand(quantityKg)}</strong>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1.25 } }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canEdit || saving || !contractNo}
          variant="contained"
          sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: alpha(ACCENT, 0.85) } }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>

      {/* Create DO cho Direct P-S vừa tạo (Save → mở popup). Đóng popup = đóng cả dialog Edit.
          Direct>0 + contract đã có DO → edit mode dùng lại DO cũ. */}
      <CreateDoFromPlanDialog
        open={doOpen}
        scenario="direct"
        mode={doMode}
        seeds={doSeeds}
        editRows={doEditRows}
        onClose={() => {
          setDoOpen(false)
          onClose()
        }}
      />

      {/* TH1: Direct=0 + contract còn DO → confirm xoá DO + update plan. */}
      <ConfirmDialog
        open={confirmDelSaveOpen}
        title="Update Purchase Plan"
        message={
          (existingDos?.length ?? 0) > 0
            ? `Direct P-S is set to 0 but this contract still has ${existingDos?.length} booked DO: ${(existingDos ?? [])
                .map((d) => d.doNo || `#${d.deliveryOrderScsId}`)
                .join(', ')}. Saving will DELETE these DO(s) and update the plan. Continue?`
            : ''
        }
        confirmLabel="Delete DO(s) + Update"
        danger
        loading={saving || deletingDos}
        onConfirm={() => {
          setConfirmDelSaveOpen(false)
          void performSaveAndDo(true)
        }}
        onClose={() => setConfirmDelSaveOpen(false)}
      />
    </>
  )
}
